import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Search, Filter, ChevronDown, Eye, X, Radio } from 'lucide-react';
import LiveStreamHost from '../../components/LiveStreamHost';

export default function DenBookings() {
  const addToast = useToast();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [showLiveStream, setShowLiveStream] = useState(false);

  useEffect(() => {
    const params = statusFilter ? `?status=${statusFilter}` : '';
    apiFetch(`/api/bookings${params}`).then(b => { setBookings(b); setLoading(false); }).catch(() => setLoading(false));
  }, [statusFilter]);

  const handleStatusChange = async (bookingId, status) => {
    try {
      await apiFetch(`/api/bookings/${bookingId}/status`, { method: 'PUT', body: JSON.stringify({ status }) });
      setBookings(bookings.map(b => b.id === bookingId ? { ...b, status } : b));
      if (selected?.id === bookingId) setSelected({ ...selected, status });
      addToast(`Booking ${status.toLowerCase()}`);
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handlePayment = async (bookingId, paymentStatus, method) => {
    try {
      await apiFetch(`/api/bookings/${bookingId}/payment`, { method: 'PUT', body: JSON.stringify({ paymentStatus, paymentMethod: method }) });
      setBookings(bookings.map(b => b.id === bookingId ? { ...b, paymentStatus } : b));
      addToast('Payment updated');
    } catch (e) { addToast(e.message, 'error'); }
  };

  const STATUSES = ['', 'REQUESTED', 'CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'];

  return (
    <div data-testid="den-bookings">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>All Bookings</h1>
        <div className="flex gap-2">
          {STATUSES.map(s => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setLoading(true); }}
              data-testid={`filter-${s || 'all'}`}
              className={`px-3 py-1.5 rounded-xl text-xs font-semibold transition-all ${statusFilter === s ? 'bg-petfolk-orange text-white' : ''}`}
              style={{ color: statusFilter !== s ? 'var(--text-secondary)' : undefined }}
            >
              {s || 'All'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3,4].map(i => <div key={i} className="skeleton h-20 rounded-2xl" />)}</div>
      ) : bookings.length === 0 ? (
        <div className="petfolk-card p-10 text-center"><p style={{ color: 'var(--text-secondary)' }}>No bookings found</p></div>
      ) : (
        <div className="space-y-3">
          {bookings.map(b => (
            <div key={b.id} className="petfolk-card p-4" data-testid={`den-booking-${b.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-petfolk-orange/15 to-petfolk-purple/10 flex items-center justify-center text-xl shrink-0">
                  {b.pet?.species === 'DOG' ? '🐕' : '🐈'}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-outfit font-bold text-sm" style={{ color: 'var(--text-primary)' }}>{b.service?.name}</p>
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        {b.pet?.name} • {b.customer?.name || 'Customer'} • {b.node?.displayName}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`badge badge-${b.status.toLowerCase().replace('_', '-')}`}>{b.status.replace('_', ' ')}</span>
                      <span className={`badge badge-${b.paymentStatus?.toLowerCase()}`}>{b.paymentStatus}</span>
                      {b.liveStreamEnabled && (
                        <span className="badge flex items-center gap-1" style={{ background: b.liveStreamStatus === 'LIVE' ? 'rgba(239,68,68,0.12)' : 'rgba(139,92,246,0.1)', color: b.liveStreamStatus === 'LIVE' ? '#EF4444' : '#8B5CF6' }}>
                          <Radio size={10} />
                          {b.liveStreamStatus === 'LIVE' ? 'LIVE' : 'Live+'}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-3">
                    <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                      {b.scheduledStart ? new Date(b.scheduledStart).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Not scheduled'}
                    </p>
                    <div className="flex items-center gap-2">
                      <span className="font-outfit font-bold text-sm" style={{ color: 'var(--text-primary)' }}>₹{b.netAmount?.toLocaleString()}</span>
                      <button onClick={() => setSelected(b)} className="p-1.5 rounded-lg hover:bg-black/5 dark:hover:bg-white/5" data-testid={`view-booking-${b.id}`}>
                        <Eye size={16} style={{ color: 'var(--text-secondary)' }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Booking Detail Modal */}
      {selected && (
        <div className="modal-overlay" onClick={() => { setSelected(null); setShowLiveStream(false); }}>
          <div className="petfolk-card w-full max-w-lg mx-4 p-6 max-h-[85vh] overflow-auto animate-slide-up" onClick={e => e.stopPropagation()} data-testid="booking-detail-modal">
            <div className="flex justify-between mb-4">
              <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>Booking Details</h2>
              <button onClick={() => { setSelected(null); setShowLiveStream(false); }}><X size={20} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Booking ID</span><span className="font-mono text-xs" style={{ color: 'var(--text-primary)' }}>{selected.id.slice(0, 12)}...</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Pet</span><span style={{ color: 'var(--text-primary)' }}>{selected.pet?.name} ({selected.pet?.species})</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Customer</span><span style={{ color: 'var(--text-primary)' }}>{selected.customer?.name} ({selected.customer?.phone})</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Service</span><span style={{ color: 'var(--text-primary)' }}>{selected.service?.name}</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Store</span><span style={{ color: 'var(--text-primary)' }}>{selected.node?.displayName}</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Schedule</span><span style={{ color: 'var(--text-primary)' }}>{selected.scheduledStart ? new Date(selected.scheduledStart).toLocaleString('en-IN') : 'TBD'}</span></div>
              <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Amount</span><span className="font-bold" style={{ color: 'var(--text-primary)' }}>₹{selected.netAmount?.toLocaleString()}</span></div>
              {selected.creditsUsed > 0 && <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Credits Used</span><span className="text-green-600">-₹{selected.creditsUsed}</span></div>}
              {selected.discountAmount > 0 && <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Discount</span><span className="text-green-600">-₹{selected.discountAmount}</span></div>}
              {selected.liveStreamEnabled && <div className="flex justify-between"><span style={{ color: 'var(--text-secondary)' }}>Live Stream</span><span className="text-petfolk-purple">Enabled</span></div>}
              {selected.notes && <div><span style={{ color: 'var(--text-secondary)' }}>Notes:</span> <span style={{ color: 'var(--text-primary)' }}>{selected.notes}</span></div>}
            </div>

            {/* Actions */}
            <div className="mt-6 pt-4 space-y-3" style={{ borderTop: '1px solid var(--border-color)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>UPDATE STATUS</p>
              <div className="flex flex-wrap gap-2">
                {['CONFIRMED', 'IN_PROGRESS', 'COMPLETED', 'CANCELLED'].map(s => (
                  <button
                    key={s}
                    onClick={() => handleStatusChange(selected.id, s)}
                    disabled={selected.status === s}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                      selected.status === s ? 'opacity-50' : 'hover:opacity-80'
                    }`}
                    style={{ background: s === 'COMPLETED' ? '#22C55E' : s === 'CANCELLED' ? '#EF4444' : s === 'IN_PROGRESS' ? '#F59E0B' : '#8B5CF6', color: 'white' }}
                    data-testid={`set-status-${s}`}
                  >
                    {s.replace('_', ' ')}
                  </button>
                ))}
              </div>
              {selected.paymentStatus === 'UNPAID' && (
                <>
                  <p className="text-xs font-semibold mt-4" style={{ color: 'var(--text-secondary)' }}>MARK PAYMENT</p>
                  <div className="flex flex-wrap gap-2">
                    {['CASH', 'UPI_AT_STORE', 'CARD_AT_STORE'].map(m => (
                      <button key={m} onClick={() => handlePayment(selected.id, 'PAID', m)} className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-green-500 text-white" data-testid={`pay-${m}`}>
                        Paid ({m.replace('_', ' ')})
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Live Stream Section */}
            {selected.liveStreamEnabled && (
              <div className="mt-4">
                {!showLiveStream ? (
                  <button
                    onClick={() => setShowLiveStream(true)}
                    className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{ background: 'linear-gradient(135deg, #EF4444, #8B5CF6)' }}
                    data-testid="open-live-stream"
                  >
                    <Radio size={16} />
                    {selected.liveStreamStatus === 'LIVE' ? '🔴 Manage Live Stream' : 'Start Live Stream'}
                  </button>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold" style={{ color: 'var(--text-secondary)' }}>LIVE STREAM PANEL</p>
                      <button onClick={() => setShowLiveStream(false)} className="text-xs" style={{ color: 'var(--text-secondary)' }}>Hide</button>
                    </div>
                    <LiveStreamHost
                      booking={selected}
                      onStreamStatusChange={(newStatus, updatedBooking) => {
                        if (updatedBooking) {
                          setSelected({ ...selected, liveStreamStatus: newStatus, ...updatedBooking });
                          setBookings(bookings.map(b => b.id === selected.id ? { ...b, liveStreamStatus: newStatus } : b));
                        }
                        addToast(newStatus === 'LIVE' ? '🔴 Stream started! Customers can now watch.' : 'Stream ended.', newStatus === 'LIVE' ? 'success' : 'info');
                      }}
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
